import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FiSave, FiRefreshCw, FiFileText, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import api from '../services/api';
import { downloadAdviesVerkoopPdf, downloadDealerPdf, downloadKostEnVerkoopExcel } from '../utils/prijsberekeningExport';
import {
  DEFAULT_PRIJSBEREKENING_SETTINGS,
  parsePrijsberekeningSettings,
  computePrijsberekeningRow,
  containerEurPerCbm,
} from '../utils/prijsberekening';
import { ProductNaamMetKlasse } from '../components/ProductKlasseIcon';

const PARAM_NAAM = 'prijsberekening_settings';

export default function ProductenPrijsberekeningTab({ products, leveranciers, searchTerm, onRefreshProducts }) {
  const [settings, setSettings] = useState({ ...DEFAULT_PRIJSBEREKENING_SETTINGS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  /** Directe feedback voor Corr. ±€; wordt gesynchroniseerd zodra producten van de server komen. */
  const [optimisticCorrectie, setOptimisticCorrectie] = useState({});
  const correctiePendingRef = useRef({});
  const [prijsberekeningParamsOpen, setPrijsberekeningParamsOpen] = useState(false);

  const levById = useMemo(() => {
    const m = {};
    (leveranciers || []).forEach((l) => {
      if (l && l.id != null) m[l.id] = l;
    });
    return m;
  }, [leveranciers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/parameters/${encodeURIComponent(PARAM_NAAM)}`).catch(() => ({ data: null }));
      const raw = res.data?.waarde;
      setSettings(parsePrijsberekeningSettings(typeof raw === 'string' ? raw : ''));
    } catch {
      setSettings({ ...DEFAULT_PRIJSBEREKENING_SETTINGS });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const list = Array.isArray(products) ? products : [];
    setOptimisticCorrectie((prev) => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      let changed = false;
      const next = { ...prev };
      for (const idStr of ids) {
        const id = Number(idStr);
        const p = list.find((x) => x && x.id === id);
        if (!p) {
          delete next[idStr];
          changed = true;
          continue;
        }
        if (correctiePendingRef.current[id]) continue;
        const server = Math.round(parseFloat(String(p.prijsberekening_advies_correctie_eur ?? 0).replace(',', '.')) || 0);
        if (next[idStr] === server) {
          delete next[idStr];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [products]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/parameters', {
        parameter_naam: PARAM_NAAM,
        waarde: JSON.stringify(settings),
        beschrijving:
          'Prijsberekening-tab: container, USD/EUR, verzend, standaard marge %, marketing %, BTW %, dealerkorting, standaard extra. Geen automatische Woo-prijs.',
      });
      alert('Instellingen opgeslagen.');
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const persistExtra = async (productId, rawValue) => {
    try {
      await api.patch(`/producten/${productId}/prijsberekening-extra`, {
        prijsberekening_extra_per_stuk: rawValue,
      });
      if (typeof onRefreshProducts === 'function') onRefreshProducts();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Opslaan extra mislukt');
    } finally {
      setEditingExtra(null);
    }
  };

  const persistAdviesCorrectie = useCallback(
    (productId, nextCorr) => {
      setOptimisticCorrectie((o) => ({ ...o, [productId]: nextCorr }));
      correctiePendingRef.current[productId] = true;
      api
        .patch(`/producten/${productId}/prijsberekening-advies-correctie`, {
          prijsberekening_advies_correctie_eur: nextCorr,
        })
        .then(() => {
          if (typeof onRefreshProducts === 'function') onRefreshProducts();
        })
        .catch((err) => {
          setOptimisticCorrectie((o) => {
            const n = { ...o };
            delete n[productId];
            return n;
          });
          alert(err.response?.data?.error || err.message || 'Opslaan correctie mislukt');
        })
        .finally(() => {
          delete correctiePendingRef.current[productId];
        });
    },
    [onRefreshProducts]
  );

  const eurPerCbm = containerEurPerCbm(settings);

  const rows = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    return list
      .filter((p) => Number(p.uitfaseren) !== 1)
      .map((p) => {
        const merged =
          optimisticCorrectie[p.id] !== undefined
            ? { ...p, prijsberekening_advies_correctie_eur: optimisticCorrectie[p.id] }
            : p;
        const lev = p.leverancier_id != null ? levById[p.leverancier_id] : null;
        const calc = computePrijsberekeningRow(merged, lev, settings);
        return { product: p, lev, calc };
      });
  }, [products, levById, settings, optimisticCorrectie]);

  if (loading) {
    return <p className="muted">Instellingen laden…</p>;
  }

  return (
    <div className="producten-prijsberekening">
      <div className="producten-prijsberekening-intro">
        <p>
          <strong>Alleen ter referentie.</strong> De kolom &quot;Verkoop (Woo)&quot; is de huidige importprijs uit WooCommerce
          (als <strong>incl. BTW</strong> beschouwd voor de vergelijking);
          <strong> niets wordt automatisch teruggeschreven</strong> naar het product.
        </p>
        <p className="muted">
          <strong>Kostprijs</strong> = inkoop (omgerekend naar €) + containerdeel. Per product vult u <strong>extra €</strong>{' '}
          (of laat leeg voor de standaard extra uit de parameters). <strong>Marge %</strong> kan per leverancier in het scherm
          Leveranciers; anders geldt de standaardmarge hieronder. <strong>Advies incl. BTW</strong> = (subtotaal na marge + marketing)
          × (1 + BTW%), optioneel <strong>afgerond naar het volgende bedrag op ,95</strong>, plus eventuele{' '}
          <strong>handmatige correctie</strong> (±€ incl. BTW; geen invloed op kostprijs of dealer). Kolom <strong>Marge €</strong> toont de{' '}
          <strong>bruto marge ex BTW</strong> na die correctie (verkoop ex BTW uit het advies incl. na correctie, min het subtotaal vóór marge %).{' '}
          <strong>Dealer</strong> blijft{' '}
          <strong>excl. BTW</strong>, berekend op het advies ex BTW (niet op de afgeronde incl.-prijs).
          Producten gemarkeerd als <strong>uitfaseren</strong> komen niet in dit overzicht (ook niet in export).
        </p>
      </div>

      <div className="producten-prijsberekening-settings-card">
        <button
          type="button"
          className="producten-prijsberekening-params-toggle"
          onClick={() => setPrijsberekeningParamsOpen((o) => !o)}
          aria-expanded={prijsberekeningParamsOpen}
          aria-controls="prijsberekening-params-panel"
          id="prijsberekening-params-toggle"
        >
          <span className="producten-prijsberekening-params-toggle-icon" aria-hidden>
            {prijsberekeningParamsOpen ? <FiChevronDown /> : <FiChevronRight />}
          </span>
          <span className="producten-prijsberekening-params-toggle-label">Parameters</span>
          {!prijsberekeningParamsOpen ? (
            <span className="producten-prijsberekening-params-toggle-hint muted"> — ingeklapt; klik om te tonen</span>
          ) : null}
        </button>
        {prijsberekeningParamsOpen ? (
          <form
            id="prijsberekening-params-panel"
            className="producten-prijsberekening-settings"
            onSubmit={handleSaveSettings}
          >
        <div className="producten-prijsberekening-grid">
          <label>
            EUR per 1 USD (koers)
            <input
              type="number"
              step="0.0001"
              min="0.0001"
              value={settings.eur_per_usd}
              onChange={(e) => setSettings({ ...settings, eur_per_usd: e.target.value })}
            />
          </label>
          <label>
            Container: totaal bedrag
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.container_total}
              onChange={(e) => setSettings({ ...settings, container_total: e.target.value })}
            />
          </label>
          <label>
            Container: valuta
            <select
              value={settings.container_currency}
              onChange={(e) => setSettings({ ...settings, container_currency: e.target.value })}
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </select>
          </label>
          <label>
            Container: max. CBM inhoud
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.container_cbm_max}
              onChange={(e) => setSettings({ ...settings, container_cbm_max: e.target.value })}
            />
          </label>
          <label>
            Verzendtoeslag DPD (€/stuk)
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.verzend_dpd}
              onChange={(e) => setSettings({ ...settings, verzend_dpd: e.target.value })}
            />
          </label>
          <label>
            Verzendtoeslag DHL (€/stuk)
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.verzend_dhl}
              onChange={(e) => setSettings({ ...settings, verzend_dhl: e.target.value })}
            />
          </label>
          <label>
            Verzendtoeslag pallet (€/stuk)
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.verzend_pallet}
              onChange={(e) => setSettings({ ...settings, verzend_pallet: e.target.value })}
            />
          </label>
          <label>
            Standaard extra €/stuk (als product geen eigen extra heeft)
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.extra_per_stuk_standaard}
              onChange={(e) => setSettings({ ...settings, extra_per_stuk_standaard: e.target.value })}
            />
          </label>
          <label>
            Standaard marge % (als leverancier geen eigen % heeft)
            <input
              type="number"
              step="0.1"
              min="0"
              value={settings.marge_pct}
              onChange={(e) => setSettings({ ...settings, marge_pct: e.target.value })}
            />
          </label>
          <label>
            Marketing (% op bedrag ná marge)
            <input
              type="number"
              step="0.1"
              min="0"
              value={settings.marketing_pct}
              onChange={(e) => setSettings({ ...settings, marketing_pct: e.target.value })}
            />
          </label>
          <label>
            BTW % (op advies ex BTW → consument incl.)
            <input
              type="number"
              step="0.1"
              min="0"
              value={settings.btw_pct}
              onChange={(e) => setSettings({ ...settings, btw_pct: e.target.value })}
            />
          </label>
          <label className="producten-prijsberekening-checkbox-label">
            <input
              type="checkbox"
              checked={settings.rond_advies_incl_btw_op_punt95 !== false}
              onChange={(e) =>
                setSettings({ ...settings, rond_advies_incl_btw_op_punt95: e.target.checked ? true : false })
              }
            />
            <span>
              Advies incl. BTW afronden op €…,95 (omhoog naar volgende ,95)
            </span>
          </label>
          <label>
            Dealerkorting % (op advies ex BTW)
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={settings.dealer_korting_pct}
              onChange={(e) => setSettings({ ...settings, dealer_korting_pct: e.target.value })}
            />
          </label>
        </div>
        <p className="producten-prijsberekening-hint muted">
          Berekend containerdeel: <strong>€{eurPerCbm.toFixed(2)}</strong> per CBM (na USD→EUR indien container USD). Marge per
          leverancier: bewerk leverancier onder menu Leveranciers.
        </p>
        <div className="producten-prijsberekening-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            <FiSave /> {saving ? 'Opslaan…' : 'Instellingen opslaan'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={load} disabled={saving}>
            <FiRefreshCw /> Herladen
          </button>
        </div>
          </form>
        ) : null}
      </div>

      {searchTerm ? (
        <p className="search-results-info producten-prijsberekening-filter-hint">
          Zelfde zoekfilter als productlijst (zonder uitfaseren): <strong>{rows.length}</strong> product(en) voor &quot;
          {searchTerm}&quot;.
        </p>
      ) : null}

      <div className="producten-prijsberekening-export-bar">
        <span className="muted">
          Export (huidige gefilterde lijst). PDF opent afdrukvenster — kies daar <strong>PDF</strong> als bestemming:
        </span>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={rows.length === 0}
          onClick={() => {
            try {
              downloadAdviesVerkoopPdf(rows, settings);
            } catch (err) {
              alert(err.message || 'PDF-export mislukt');
            }
          }}
        >
          <FiFileText /> PDF verkoopprijslijst
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={rows.length === 0}
          onClick={() => {
            try {
              downloadDealerPdf(rows, settings);
            } catch (err) {
              alert(err.message || 'PDF-export mislukt');
            }
          }}
        >
          <FiFileText /> PDF dealerprijslijst
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={rows.length === 0}
          onClick={() => {
            try {
              downloadKostEnVerkoopExcel(rows, settings);
            } catch (err) {
              alert(err.message || 'Excel-export mislukt');
            }
          }}
        >
          <FiFileText /> Excel kost- &amp; verkoopprijzen
        </button>
      </div>

      <div className="table-container producten-prijsberekening-table-wrap">
        <table className="data-table producten-prijsberekening-table">
          <thead>
            <tr>
              <th>Naam</th>
              <th>SKU</th>
              <th>Lev.</th>
              <th title="Leverancier valuta">V</th>
              <th title="Inkoop in valuta leverancier">Ink.</th>
              <th>Ink. €</th>
              <th>CBM</th>
              <th>Cont. €</th>
              <th className="producten-prijsberekening-kostprijs">Kostpr. €</th>
              <th title="Extra € per product (klik); leeg = standaard">Extra €</th>
              <th>Verz.</th>
              <th title="Bruto marge ex BTW na handmatige correctie: advies incl. na correctie ÷ (1 + BTW%) − subtotaal vóór marge %">
                Marge €
              </th>
              <th>Mkt €</th>
              <th title="Advies consument ex BTW">Adv. ex</th>
              <th title="Handmatige correctie op advies incl. BTW (heel €); geen invloed op kostprijs">Corr. ±€</th>
              <th
                title={
                  (settings.rond_advies_incl_btw_op_punt95 !== false
                    ? 'Advies incl. BTW (basis na ,95) + handmatige correctie'
                    : 'Advies incl. BTW + handmatige correctie') + '; Diff t.o.v. Woo'
                }
              >
                Adv. incl.
              </th>
              <th title="Dealer excl. BTW">Dealer ex</th>
              <th title="Woo verkoop (incl. BTW)">Woo incl.</th>
              <th title="Woo − advies incl. (na correctie)">Diff</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={19} className="empty-state">
                  Geen producten.
                </td>
              </tr>
            ) : (
              rows.map(({ product: p, calc }) => (
                <tr key={p.id}>
                  <td className="producten-prijsberekening-naam">
                    <ProductNaamMetKlasse productId={p.id} naam={p.naam || '—'} product_klasse={p.product_klasse} />
                  </td>
                  <td>{p.sku || '—'}</td>
                  <td>{p.leverancier_naam || '—'}</td>
                  <td>{calc.valuta}</td>
                  <td>{inkoopFmt(calc.valuta, calc.inkoop_raw)}</td>
                  <td>{euro(calc.inkoop_eur)}</td>
                  <td>{p.cbm_per_stuk != null && p.cbm_per_stuk !== '' ? Number(p.cbm_per_stuk).toFixed(3) : '—'}</td>
                  <td>{euro(calc.container_alloc_eur)}</td>
                  <td className="producten-prijsberekening-kostprijs">{euro(calc.kostprijs_eur)}</td>
                  <td
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingExtra({
                        productId: p.id,
                        value:
                          p.prijsberekening_extra_per_stuk != null && p.prijsberekening_extra_per_stuk !== ''
                            ? String(p.prijsberekening_extra_per_stuk)
                            : '',
                      });
                    }}
                    style={{ cursor: 'pointer', minWidth: '72px' }}
                    title="Klik om extra € op dit product te zetten; leeg laten = standaard uit parameters"
                  >
                    {editingExtra?.productId === p.id ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editingExtra.value}
                        onChange={(e) => setEditingExtra({ ...editingExtra, value: e.target.value })}
                        onBlur={() => {
                          const cur = p.prijsberekening_extra_per_stuk;
                          const curS = cur != null && cur !== '' ? String(cur) : '';
                          if (editingExtra.value.trim() !== curS) {
                            persistExtra(p.id, editingExtra.value.trim() === '' ? null : editingExtra.value);
                          } else {
                            setEditingExtra(null);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.target.blur();
                          else if (e.key === 'Escape') setEditingExtra(null);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                        style={{ width: '76px', padding: '2px 4px', border: '1px solid #3FDFCF', borderRadius: '4px' }}
                      />
                    ) : (
                      <span>
                        {euro(calc.extra_per_stuk)}
                        {p.prijsberekening_extra_per_stuk == null || p.prijsberekening_extra_per_stuk === '' ? (
                          <span className="muted" style={{ fontSize: '0.75rem', display: 'block' }}>
                            (std.)
                          </span>
                        ) : null}
                      </span>
                    )}
                  </td>
                  <td>{euro(calc.verzend_eur)}</td>
                  <td className={diffClass(calc.marge_werkelijk_ex_btw_eur)}>{euro(calc.marge_werkelijk_ex_btw_eur)}</td>
                  <td>{euro(calc.marketing_eur)}</td>
                  <td>{euro(calc.advies_verkoop_ex_btw_eur)}</td>
                  <td className="producten-prijsberekening-corr-cell">
                    <div className="producten-prijsberekening-corr" role="group" aria-label="Correctie advies incl. BTW per euro">
                      <button
                        type="button"
                        className="producten-prijsberekening-corr-btn"
                        onClick={() =>
                          persistAdviesCorrectie(p.id, (calc.advies_correctie_eur_incl_btw || 0) - 1)
                        }
                        title="€1 minder incl. BTW"
                        aria-label="Correctie een euro verlagen"
                      >
                        −
                      </button>
                      <span className="producten-prijsberekening-corr-val" title="Correctie t.o.v. berekend advies incl. BTW">
                        {correctieFmt(calc.advies_correctie_eur_incl_btw)}
                      </span>
                      <button
                        type="button"
                        className="producten-prijsberekening-corr-btn"
                        onClick={() =>
                          persistAdviesCorrectie(p.id, (calc.advies_correctie_eur_incl_btw || 0) + 1)
                        }
                        title="€1 meer incl. BTW"
                        aria-label="Correctie een euro verhogen"
                      >
                        +
                      </button>
                    </div>
                  </td>
                  <td className="producten-prijsberekening-adv-incl-cell">
                    {calc.advies_correctie_eur_incl_btw !== 0 ? (
                      <span className="producten-prijsberekening-adv-incl-inner">
                        <span className="producten-prijsberekening-adv-basis">{euro(calc.advies_verkoop_incl_btw_basis_eur)}</span>{' '}
                        <strong>{euro(calc.advies_verkoop_incl_btw_eur)}</strong>
                      </span>
                    ) : (
                      <strong>{euro(calc.advies_verkoop_incl_btw_eur)}</strong>
                    )}
                  </td>
                  <td>{euro(calc.dealer_prijs_ex_btw_eur)}</td>
                  <td>{euro(calc.verkoop_woo)}</td>
                  <td className={diffClass(calc.marge_vs_advies)}>{diffFmt(calc.marge_vs_advies)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function inkoopFmt(valuta, n) {
  if (n == null || Number.isNaN(n)) return '—';
  const sym = String(valuta).toUpperCase() === 'USD' ? '$' : '€';
  return `${sym}${Number(n).toFixed(2)}`;
}

function euro(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `€${Number(n).toFixed(2)}`;
}

function correctieFmt(n) {
  if (n == null || Number.isNaN(n) || n === 0) return '0';
  const v = Math.round(Number(n));
  return v > 0 ? `+${v}` : String(v);
}

function diffFmt(v) {
  if (v == null || Number.isNaN(v)) return '—';
  const s = v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2);
  return s;
}

function diffClass(v) {
  if (v == null || Number.isNaN(v)) return '';
  if (v >= 0) return 'producten-prijsberekening-diff-pos';
  return 'producten-prijsberekening-diff-neg';
}
